/**
 * API Service for MFU Template System
 * Base API URL configuration and request helpers
 */

// ⚠️ สลับระหว่าง localhost และ production
// 🔹 สำหรับ localhost: uncomment บรรทัดที่ 1, comment บรรทัดที่ 2
// 🔹 สำหรับ production: comment บรรทัดที่ 1, uncomment บรรทัดที่ 2

//const API_BASE_URL = "http://localhost:8000"; // [1] localhost
const API_BASE_URL = "https://mfuwellnesscenter-server.dietz.asia"; // [2] production

// MFU OpenAPI Base URL (from httpie-collection-mfu.json)
const MFU_OPENAPI_BASE_URL = "https://server.dietz.asia/openApi/mfu/v1.0.0";
const MFU_OPENAPI_AUTH = "DDQ8y7V19LaWvI*j";

/**
 * Helper function สำหรับ fetch API
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    const responseText = await response.text();

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error("❌ Response ไม่ใช่ JSON:");
      console.error("URL:", url);
      console.error("Status:", response.status);
      console.error(
        "Response Text (first 500 chars):",
        responseText.substring(0, 500)
      );

      // ตรวจสอบว่าเป็น HTML error page หรือไม่
      if (
        responseText.includes("<!DOCTYPE") ||
        responseText.includes("<html") ||
        responseText.includes("<br")
      ) {
        throw new Error(
          "Backend error: ส่ง HTML กลับมาแทน JSON\n\nกรุณาตรวจสอบ:\n1. Backend server ทำงานปกติ\n2. Database connection\n3. PHP errors ใน terminal"
        );
      }

      throw new Error(`Response ไม่ถูกต้อง: ${responseText.substring(0, 100)}`);
    }

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return data;
  } catch (error) {
    console.error("❌ API Error:", url, error.message);
    throw error;
  }
};

/**
 * Template API
 */
export const templateAPI = {
  /**
   * ดึงรายการ templates ทั้งหมด
   */
  getAll: async () => {
    return await apiRequest("/api/templates");
  },

  /**
   * ดึง template เดี่ยว
   */
  getById: async (id) => {
    return await apiRequest(`/api/templates/${id}`);
  },

  /**
   * สร้าง template ใหม่
   */
  create: async (templateData) => {
    return await apiRequest("/api/templates", {
      method: "POST",
      body: JSON.stringify(templateData),
    });
  },

  /**
   * แก้ไข template
   */
  update: async (id, templateData) => {
    return await apiRequest(`/api/templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(templateData),
    });
  },

  /**
   * ลบ template
   */
  delete: async (id) => {
    return await apiRequest(`/api/templates/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * เปิด/ปิด template
   */
  toggleActive: async (id) => {
    return await apiRequest(`/api/templates/${id}/toggle`, {
      method: "PATCH",
    });
  },
};

/**
 * Activity Logs API
 */
export const activityLogsAPI = {
  /**
   * ดึง activity logs
   */
  getAll: async () => {
    return await apiRequest("/api/activity-logs");
  },
};

/**
 * Reference Data API
 */
export const referenceDataAPI = {
  /**
   * ดึงข้อมูลพารามิเตอร์ทั้งหมด
   */
  getAllParameters: async () => {
    return await apiRequest("/api/reference/parameters");
  },

  /**
   * ดึงรายชื่อพารามิเตอร์สำหรับ dropdown
   * @param {Object} options - { gender: 'F'|'M', age: number }
   */
  getParametersList: async (options = {}) => {
    const { gender, age, minAge, maxAge } = options;
    let url = "/api/reference/parameters/list";

    // สร้าง query string
    const params = new URLSearchParams();
    if (gender) params.append("gender", gender);
    if (age !== undefined && age !== null) params.append("age", age);
    if (minAge !== undefined && minAge !== null)
      params.append("minAge", minAge);
    if (maxAge !== undefined && maxAge !== null)
      params.append("maxAge", maxAge);

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return await apiRequest(url);
  },

  /**
   * ค้นหาค่าปกติตามเพศและอายุ
   */
  searchNormalValues: async (searchData) => {
    return await apiRequest("/api/reference/parameters/search", {
      method: "POST",
      body: JSON.stringify(searchData),
    });
  },

  /**
   * ดึงรายชื่อหมวดหมู่
   */
  getCategories: async () => {
    return await apiRequest("/api/reference/categories");
  },
};

/**
 * Tags API
 * ใช้เฉพาะ template_tags (ไม่มี table tags แล้ว)
 */
export const tagsAPI = {
  /**
   * ดึง tags ที่เคยถูกใช้ใน templates แล้ว (จาก template_tags)
   */
  getUsedTags: async () => {
    return await apiRequest("/api/tags/used");
  },
};

/**
 * Health Records API
 */
export const healthRecordsAPI = {
  /**
   * สร้าง/อัพเดท health record จากข้อมูล MFU API (Option 2: Hybrid System)
   * @param {Object} mfuData - ข้อมูลจาก MFU API ที่จัดรูปแบบจาก Frontend
   * @returns {Promise} - { record_id }
   */
  createFromMFUData: async (mfuData) => {
    return await apiRequest("/api/health-records/create-from-mfu", {
      method: "POST",
      body: JSON.stringify(mfuData),
    });
  },

  /**
   * ดึงข้อมูล Lab Results จาก MFU API ผ่าน Backend Proxy (แก้ปัญหา CORS)
   * @param {string} searchKey - NID หรือ HN
   * @returns {Promise} - Lab data in MFU API format
   */
  fetchFromMFUAPI: async (searchKey) => {
    return await apiRequest("/api/mfu-proxy/lab-results", {
      method: "POST",
      body: JSON.stringify({ searchKey: searchKey }),
    });
  },

  /**
   * ดึงข้อมูลสุขภาพทั้งหมดจากฐานข้อมูล
   */
  getAll: async () => {
    return await apiRequest("/api/health-records");
  },

  /**
   * ดึงข้อมูลสุขภาพตาม NID
   * @param {string} nid - National ID (encrypted)
   */
  getByNID: async (nid) => {
    return await apiRequest(
      `/api/health-records/by-nid/${encodeURIComponent(nid)}`
    );
  },

  /**
   * ดึงข้อมูลสุขภาพตาม ID
   * @param {number} id - Health record ID
   */
  getById: async (id) => {
    return await apiRequest(`/api/health-records/${id}`);
  },

  /**
   * ลบข้อมูลสุขภาพ
   * @param {number} id - Health record ID
   */
  delete: async (id) => {
    return await apiRequest(`/api/health-records/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * ซ่อน template สำหรับ health record นี้
   * @param {number} recordId - Health record ID
   * @param {number} templateId - Template ID
   * @param {string} templateName - Template name
   */
  hideTemplate: async (recordId, templateId, templateName) => {
    return await apiRequest(`/api/health-records/${recordId}/templates/hide`, {
      method: "POST",
      body: JSON.stringify({
        template_id: templateId,
        template_name: templateName,
      }),
    });
  },

  /**
   * แสดง template ที่ซ่อนไว้
   * @param {number} recordId - Health record ID
   * @param {number} templateId - Template ID
   */
  unhideTemplate: async (recordId, templateId) => {
    return await apiRequest(
      `/api/health-records/${recordId}/templates/${templateId}/unhide`,
      {
        method: "POST",
      }
    );
  },

  /**
   * ดึงรายการ template ที่ซ่อนไว้
   * @param {number} recordId - Health record ID
   */
  getHiddenTemplates: async (recordId) => {
    return await apiRequest(`/api/health-records/${recordId}/templates/hidden`);
  },

  /**
   * ดึงรายการ manual templates (templates ที่หมอเลือกเอง)
   * @param {number} recordId - Health record ID (from health_recommendations)
   */
  getManualTemplates: async (recordId) => {
    return await apiRequest(`/api/health-records/${recordId}/templates/manual`);
  },

  /**
   * บันทึก manual templates (ที่หมอเลือกเอง)
   * @param {number} recordId - Health record ID
   * @param {Array} templateIds - Array of template IDs
   */
  saveManualTemplates: async (recordId, templateIds) => {
    return await apiRequest(
      `/api/health-records/${recordId}/templates/manual`,
      {
        method: "POST",
        body: JSON.stringify({ template_ids: templateIds }),
      }
    );
  },

  /**
   * บันทึก manual templates โดยใช้ HN หรือ NID (สำหรับผู้ใช้ที่ข้อมูลมาจาก MFU API โดยตรง)
   * @param {string} identifier - HN หรือ NID
   * @param {Array} templateIds - Array of template IDs
   * @param {Object} patientData - ข้อมูลผู้ป่วยสำหรับสร้าง record ถ้ายังไม่มี (optional)
   */
  saveManualTemplatesByIdentifier: async (identifier, templateIds, patientData = null) => {
    return await apiRequest(
      `/api/health-records/templates/manual-by-identifier`,
      {
        method: "POST",
        body: JSON.stringify({
          identifier: identifier,
          template_ids: templateIds,
          patient_data: patientData
        }),
      }
    );
  },

  /**
   * ดึง record_id จาก health_records โดยใช้ HN หรือ NID
   * @param {string} identifier - HN หรือ NID
   */
  getRecordIdByIdentifier: async (identifier, visitDate = null) => {
    const url = visitDate
      ? `/api/health-records/record-id/${encodeURIComponent(identifier)}?visitDate=${encodeURIComponent(visitDate)}`
      : `/api/health-records/record-id/${encodeURIComponent(identifier)}`;
    return await apiRequest(url);
  },
};

/**
 * Normal Range API - ดึงค่าปกติจาก medium.json
 */
export const normalRangeAPI = {
  /**
   * ดึงค่าปกติสำหรับหลาย parameters พร้อมกัน
   * @param {Array} tests - [{ name: string, sex: string, age: number }]
   */
  getBatch: async (tests) => {
    return await apiRequest("/api/normal-ranges", {
      method: "POST",
      body: JSON.stringify({ tests }),
    });
  },
};

/**
 * Health Recommendation API - จัดการคำแนะนำสุขภาพและสถานะการอนุมัติ
 */
export const healthRecommendationAPI = {
  /**
   * สร้างคำแนะนำสุขภาพด้วย AI โดยใช้ HN และ visit_date (สำหรับ Option 2: Hybrid System)
   * @param {string} hn - Hospital Number
   * @param {string} visitDate - วันที่ตรวจ (YYYY-MM-DD)
   */
  generateRecommendationsByVisit: async (hn, visitDate, visitDateTime = null) => {
    const payload = { hn, visit_date: visitDate };
    if (visitDateTime) {
      payload.visit_datetime = visitDateTime;
    }

    return await apiRequest(
      "/api/health-records/generate-recommendations-by-visit",
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  },

  /**
   * สร้างคำแนะนำสุขภาพด้วย AI (เดิม - ใช้ record ID)
   * @param {number} recordId - Health record ID
   */
  generateRecommendations: async (recordId) => {
    return await apiRequest(
      `/api/health-records/${recordId}/generate-recommendations`,
      {
        method: "POST",
      }
    );
  },

  /**
   * บันทึก draft recommendations
   * @param {number} recordId - Health record ID
   * @param {Object} recommendations - { doctor_advice, nutrition_advice, exercise_advice, sleep_advice }
   * @param {string} updatedBy - ผู้ที่แก้ไข
   */
  saveDraft: async (recordId, recommendations, updatedBy = "admin") => {
    return await apiRequest(
      `/api/health-records/${recordId}/recommendations/draft`,
      {
        method: "POST",
        body: JSON.stringify({
          ...recommendations,
          updated_by: updatedBy,
        }),
      }
    );
  },

  /**
   * ดึง draft recommendations
   * @param {number} recordId - Health record ID
   */
  getDraft: async (recordId) => {
    return await apiRequest(
      `/api/health-records/${recordId}/recommendations/draft`
    );
  },

  /**
   * อนุมัติและส่งผลสุขภาพ
   * @param {number} recordId - Health record ID
   * @param {Object} recommendations - { doctor_advice, nutrition_advice, exercise_advice, sleep_advice }
   * @param {string} approvedBy - ผู้อนุมัติ
   * @param {Array} templateIds - Template IDs ที่ใช้
   */
  approveAndSend: async (
    recordId,
    recommendations,
    approvedBy = "admin",
    templateIds = []
  ) => {
    return await apiRequest(
      `/api/health-records/${recordId}/recommendations/approve-and-send`,
      {
        method: "POST",
        body: JSON.stringify({
          ...recommendations,
          approved_by: approvedBy,
          template_ids: templateIds,
        }),
      }
    );
  },

  /**
   * อนุมัติและส่งผลสุขภาพพร้อมส่ง LINE Notification
   * @param {number} recordId - Health record ID
   * @param {Object} recommendations - { doctor_advice, nutrition_advice, exercise_advice, sleep_advice }
   * @param {string} approvedBy - ผู้อนุมัติ
   * @param {Array} templateIds - Template IDs ที่ใช้
   * @param {string} lineUserId - LINE User ID สำหรับส่งการแจ้งเตือน
   */
  approveAndSendWithLine: async (
    recordId,
    recommendations,
    approvedBy = "admin",
    templateIds = [],
    lineUserId = null,
    visitDateadd = null,
    healthSnapshot = null
  ) => {
    return await apiRequest(
      `/api/health-records/${recordId}/recommendations/approve-and-send-with-line`,
      {
        method: "POST",
        body: JSON.stringify({
          ...recommendations,
          approved_by: approvedBy,
          template_ids: templateIds,
          lineUserId: lineUserId,
          visit_dateadd: visitDateadd,
          health_snapshot: healthSnapshot,
        }),
      }
    );
  },

  /**
   * ดึงประวัติการส่งผลสุขภาพทั้งหมด
   * @param {number} recordId - Health record ID
   */
  getHistory: async (recordId) => {
    return await apiRequest(
      `/api/health-records/${recordId}/recommendations/history`
    );
  },
};

/**
 * Helper function สำหรับเรียก MFU OpenAPI
 */
const mfuApiRequest = async (endpoint, options = {}) => {
  const url = `${MFU_OPENAPI_BASE_URL}${endpoint}`;

  const defaultOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: MFU_OPENAPI_AUTH,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    console.log("✅ MFU API Response:", url, data);
    return data;
  } catch (error) {
    console.error("❌ MFU API Error:", url, error.message);
    throw error;
  }
};

/**
 * Campaign API (from httpie-collection-mfu.json)
 */
export const campaignAPI = {
  /**
   * ดึงรายการแคมเปญ
   * @param {number} pageNow - หน้าที่ต้องการดึง
   */
  getList: async (pageNow = 1) => {
    return await mfuApiRequest("/CampaignList.php", {
      body: JSON.stringify({ pageNow }),
    });
  },

  /**
   * เพิ่มแคมเปญใหม่
   * @param {Object} campaignData - ข้อมูลแคมเปญ
   */
  add: async (campaignData) => {
    return await mfuApiRequest("/CampaignAdd.php", {
      body: JSON.stringify(campaignData),
    });
  },

  /**
   * แก้ไขแคมเปญ
   * @param {Object} campaignData - ข้อมูลแคมเปญ (ต้องมี id)
   */
  update: async (campaignData) => {
    return await mfuApiRequest("/CampaignUpdate.php", {
      body: JSON.stringify(campaignData),
    });
  },
};

export const memberAPI = {
  /**
   * ดึงรายการสมาชิก/ผู้รับการตรวจ
   * @param {Object} filters - ตัวกรองข้อมูล
   */
  getList: async (filters = {}) => {
    return await mfuApiRequest("/MemberList.php", {
      body: JSON.stringify(filters),
    });
  },

  /**
   * ดึงรายการผลแลป (Lab Results) ของสมาชิก
   * @param {Object} filters - ตัวกรองข้อมูล (dateStart, dateStop, keywordCampaign, keywordName)
   */
  getLabList: async (filters = {}) => {
    return await apiRequest("/api/mfu-proxy/member-lab-list", {
      method: "POST",
      body: JSON.stringify(filters),
    });
  },

  /**
   * ดึงรายการผลแลปที่ filter ตาม approval_status (approved / revised)
   * Backend query DB เรา → cross-reference MFU API → return เฉพาะที่ match
   * @param {Object} filters - { status, dateStart, dateStop, keywordName, keywordCampaign, pageNow }
   */
  getLabListByStatus: async (filters = {}) => {
    return await apiRequest("/api/health-records/by-status", {
      method: "POST",
      body: JSON.stringify(filters),
    });
  },
};

/**
 * PDF API - สร้างและดาวน์โหลด PDF รายงานสุขภาพ
 */
export const pdfAPI = {
  /**
   * ตรวจสอบว่าเป็น mobile หรือ LIFF browser
   * @returns {boolean}
   */
  isMobileOrLIFF: () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isLIFF = /Line/i.test(userAgent) || window.liff !== undefined;
    return isMobile || isLIFF;
  },

  /**
   * Generate และ Download PDF รายงานสุขภาพ
   * - PC: ใช้ blob download (เหมือนเดิม)
   * - Mobile/LIFF: save ไฟล์บน server แล้วเปิด URL ตรง
   * @param {Object} reportData - ข้อมูลรายงานสุขภาพทั้งหมด
   * @returns {Promise<Blob|Object>} - PDF file as blob (PC) or response object (mobile)
   */
  downloadHealthReport: async (reportData) => {
    try {
      if (pdfAPI.isMobileOrLIFF()) {
        console.log('📱 Mobile/LIFF detected - using save & open method');

        // สำหรับ mobile: save PDF บน server แล้วเปิด URL
        const saveUrl = `${API_BASE_URL}/api/pdf/save-health-report`;
        const saveResponse = await fetch(saveUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(reportData),
        });

        if (!saveResponse.ok) {
          throw new Error(`HTTP ${saveResponse.status}: ${saveResponse.statusText}`);
        }

        const result = await saveResponse.json();

        if (result.status === 'success' && result.data?.full_url) {
          // เปิด PDF URL ในแท็บใหม่ - browser จะจัดการ download/preview
          window.open(result.data.full_url, '_blank');
          console.log('✅ PDF opened in new tab:', result.data.full_url);
          return result;
        } else {
          throw new Error('ไม่ได้รับ URL จาก server');
        }
      }

      // สำหรับ PC: ใช้ blob download เหมือนเดิม
      console.log('🖥️ PC detected - using blob download method');
      const url = `${API_BASE_URL}/api/pdf/health-report`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // ได้ blob กลับมา
      const blob = await response.blob();

      const filename = `health-report-${reportData.hn || 'report'}-${new Date().toISOString().slice(0, 10)}.pdf`;

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return blob;
    } catch (error) {
      console.error('❌ PDF Download Error:', error);
      throw error;
    }
  },

  /**
   * บันทึก PDF ไว้ในเซิร์ฟเวอร์
   * @param {Object} reportData - ข้อมูลรายงานสุขภาพ
   * @returns {Promise<Object>} - Response with file path
   */
  saveHealthReport: async (reportData) => {
    return await apiRequest('/api/pdf/save-health-report', {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
  },

  /**
   * ดาวน์โหลด PDF ที่บันทึกไว้แล้ว
   * @param {string} filename - ชื่อไฟล์
   */
  downloadExisting: async (filename) => {
    const url = `${API_BASE_URL}/api/pdf/download/${filename}`;
    window.open(url, '_blank');
  },

  /**
   * ลบไฟล์ PDF
   * @param {string} filename - ชื่อไฟล์
   */
  deletePDF: async (filename) => {
    return await apiRequest(`/api/pdf/delete/${filename}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Consultant API - ดึงข้อมูลที่ปรึกษา (admin_doctor)
 */
export const consultantAPI = {
  /**
   * ดึงรายการที่ปรึกษา
   * @param {Object} filters - { pageNow, keywordName, ... }
   */
  getList: async (filters = {}) => {
    return await mfuApiRequest("/ConsultList.php", {
      body: JSON.stringify(filters),
    });
  },
};

export default {
  templateAPI,
  activityLogsAPI,
  referenceDataAPI,
  tagsAPI,
  healthRecordsAPI,
  normalRangeAPI,
  healthRecommendationAPI,
  campaignAPI,
  memberAPI,
  consultantAPI,
  pdfAPI,
};
